import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Link,
  Paper,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import axios from 'axios';
import PasswordTextField from './PasswordTextField';

const API_URL = process.env.REACT_APP_API_URL ?? '';

type ResetState = 'checking' | 'valid' | 'invalid' | 'success';

const ResetPassword: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resetState, setResetState] = useState<ResetState>('checking');
  const redirectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;

    const verifySession = async () => {
      try {
        await axios.get(`${API_URL}/api/auth/password-reset/status`);
        if (active) {
          setResetState('valid');
        }
      } catch {
        if (active) {
          setResetState('invalid');
        }
      }
    };

    void verifySession();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, []);

  const validateForm = () => {
    if (!password || !confirmPassword) {
      setError('すべての項目を入力してください。');
      return false;
    }
    if (password !== confirmPassword) {
      setError('パスワードが一致しません。');
      return false;
    }
    if (password.length < 8) {
      setError('パスワードは8文字以上である必要があります。');
      return false;
    }

    const typeCount = [
      /[a-zA-Z]/.test(password),
      /\d/.test(password),
      /[!@#$%^&*(),.?":{}|<>]/.test(password),
    ].filter(Boolean).length;
    if (typeCount < 2) {
      setError('パスワードは英字・数字・記号のうち、少なくとも2種類を含む必要があります。');
      return false;
    }
    return true;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/password-reset/complete`, {
        newPassword: password,
      });
      setResetState('success');
      redirectTimeoutRef.current = setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        const detail = err.response?.data?.detail;
        if (detail === 'reset_token_invalid_or_expired') {
          setResetState('invalid');
          return;
        }
        if (Array.isArray(detail)) {
          setError(detail.map((item: { msg?: string }) => item.msg).join(' '));
        } else {
          setError(detail || 'パスワードのリセットに失敗しました');
        }
      } else {
        setError('パスワードのリセットに失敗しました');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (resetState === 'checking') {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
        <CircularProgress aria-label="リセットリンクを確認中" />
      </Container>
    );
  }

  if (resetState === 'invalid') {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h5" component="h1" align="center" gutterBottom>
            無効なリンク
          </Typography>
          <Alert severity="error" sx={{ mb: 3 }}>
            パスワードリセットリンクが無効か期限切れです。再設定メールを再度要求してください。
          </Alert>
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Link component={RouterLink} to="/request-password-reset" variant="body2">
              新しいリセットリンクを要求する
            </Link>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" component="h1" align="center" gutterBottom>
          新しいパスワードの設定
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {resetState === 'success' ? (
          <Alert severity="success" sx={{ mb: 3 }}>
            パスワードが正常に変更されました。ログイン画面に移動します...
          </Alert>
        ) : (
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Typography variant="body1" sx={{ mb: 2 }}>
              新しいパスワードを入力してください。
            </Typography>
            <PasswordTextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="新しいパスワード"
              type="password"
              id="password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={isLoading}
              helperText="8文字以上で、英字・数字・記号のうち2種類以上を含む必要があります"
            />
            <PasswordTextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="パスワード（確認）"
              type="password"
              id="confirmPassword"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              disabled={isLoading}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, py: 1.5 }}
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'パスワードを変更'}
            </Button>
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Link component={RouterLink} to="/login" variant="body2">
                ログイン画面に戻る
              </Link>
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default ResetPassword;
